import { faArrowUp } from '@fortawesome/pro-solid-svg-icons/faArrowUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import ExpandableTextArea from 'components/common/expandable_text_area';
import ModalCard from 'components/common/modal_card';
import PortalModalWrapper from 'components/lib/portal_modal_wrapper';
import TeamAssistIcon from 'components/lib/icons/team_assist_icon';
import MessageBubble, { AssistantRow } from 'components/customer/team_assist/team_assist_message';
import TeamAssistEmptyStateIcon from 'components/customer/team_assist/team_assist_empty_state_icon';
import {
  BetaTag,
  CannedResponseButton,
  CannedResponseButtons,
  TeamAssistActionsDropdown,
  TeamAssistActionsMenuIcon,
  TeamAssistActionsMenuOpener,
  TeamAssistModalActionsMenu,
} from 'components/customer/team_assist/team_assist_panel_shared';

function TeamAssistModal({
  cannedResponses,
  customerName,
  inputValue,
  isLoading,
  menuOptions,
  messages,
  messagesEndRef,
  onCannedResponse,
  onClose,
  onInputChange,
  onKeyDown,
  onSelectAction,
  onSubmit,
  onUse,
}) {
  const messagesAreaRef = useRef(null);
  const prevLoadingRef = useRef(isLoading);
  const hasScrolledSinceMountRef = useRef(false);
  const lastMessageId = messages.length ? messages[messages.length - 1].id : null;

  useEffect(() => {
    if (!messagesAreaRef.current) {
      return;
    }

    const justFinishedLoading = prevLoadingRef.current && !isLoading;
    prevLoadingRef.current = isLoading;
    const isInitialOrReturning = !hasScrolledSinceMountRef.current;
    if (isInitialOrReturning) {
      hasScrolledSinceMountRef.current = true;
    }
    const behavior = justFinishedLoading || isInitialOrReturning ? 'auto' : 'smooth';
    const container = messagesAreaRef.current;
    if (container.scrollTo) {
      container.scrollTo({ top: container.scrollHeight, behavior });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  }, [isLoading, lastMessageId]);

  return (
    <PortalModalWrapper>
      <TeamAssistModalCardWrapper>
        <ModalCard data-aid="teamAssistModal">
          <ModalContent>
            <ModalHeader>
              <ModalHeaderIcon height="20" width="20" />
              <ModalTitleWrap>
                <ModalTitle>
                  Team Assist <BetaTag>Beta</BetaTag>
                </ModalTitle>
                <ModalSubtitle>{customerName || 'this customer'}</ModalSubtitle>
              </ModalTitleWrap>
              <ModalHeaderActions>
                {messages.length > 0 && (
                  <TeamAssistActionsDropdown
                    data-aid="teamAssistModalActionsMenu"
                    menuComponent={TeamAssistModalActionsMenu}
                    onSelect={onSelectAction}
                    openerComponent={TeamAssistActionsMenuOpener}
                    options={menuOptions}
                    position="bottom"
                    text={<TeamAssistActionsMenuIcon />}
                    value=""
                  />
                )}
                <ModalCloseButton data-aid="teamAssistModalCloseButton" onClick={onClose} title="Close">
                  <i className="fa fa-times" />
                </ModalCloseButton>
              </ModalHeaderActions>
            </ModalHeader>
            <ModalMessagesArea ref={messagesAreaRef}>
              <ModalMessagesInner>
                {messages.length === 0 && !isLoading && (
                  <EmptyState>
                    <EmptyStateIconWrapper>
                      <TeamAssistEmptyStateIcon size={100} />
                    </EmptyStateIconWrapper>
                    <EmptyStateText>Ask anything about this customer—answers, history, or next steps.</EmptyStateText>
                  </EmptyState>
                )}
                {messages.map(msg => (
                  <MessageBubble key={msg.id} msg={msg} onUse={onUse} />
                ))}
                {isLoading && (
                  <AssistantRow>
                    <LoadingDots>
                      <Dot style={{ animationDelay: '0s' }} />
                      <Dot style={{ animationDelay: '0.2s' }} />
                      <Dot style={{ animationDelay: '0.4s' }} />
                    </LoadingDots>
                  </AssistantRow>
                )}
                <div ref={messagesEndRef} />
              </ModalMessagesInner>
            </ModalMessagesArea>
            <ModalInputArea>
              {messages.length === 0 && !isLoading && cannedResponses && cannedResponses.length > 0 && (
                <CannedResponseButtons>
                  {cannedResponses.map((cr, idx) => (
                    <CannedResponseButton
                      data-aid="teamAssistCannedResponseButton"
                      key={idx}
                      onClick={() => onCannedResponse(cr)}
                      type="button"
                    >
                      {cr.label}
                    </CannedResponseButton>
                  ))}
                </CannedResponseButtons>
              )}
              <ModalInputWrapper>
                <ModalTextArea
                  data-aid="teamAssistModalInput"
                  onChange={onInputChange}
                  onKeyDown={onKeyDown}
                  placeholder="Chat with Gladly AI"
                  value={inputValue}
                />
                <ModalSendButton
                  data-aid="teamAssistModalSendButton"
                  disabled={!inputValue.trim() || isLoading}
                  onClick={onSubmit}
                >
                  <FontAwesomeIcon icon={faArrowUp} style={{ fontSize: '16px' }} />
                </ModalSendButton>
              </ModalInputWrapper>
            </ModalInputArea>
          </ModalContent>
        </ModalCard>
      </TeamAssistModalCardWrapper>
    </PortalModalWrapper>
  );
}

TeamAssistModal.propTypes = {
  cannedResponses: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      message: PropTypes.string,
    })
  ),
  customerName: PropTypes.string,
  inputValue: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.string,
      id: PropTypes.string,
      role: PropTypes.string,
      sessionId: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ).isRequired,
  messagesEndRef: PropTypes.object,
  onCannedResponse: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  onSelectAction: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onUse: PropTypes.func,
};

TeamAssistModal.defaultProps = {
  cannedResponses: [],
  isLoading: false,
  messages: [],
};

export default TeamAssistModal;

const TeamAssistModalCardWrapper = styled.div`
  .modalCard-content {
    padding: 0;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 90vh;
  max-width: 1200px;
  min-width: 400px;
  overflow: hidden;
  width: 80vw;
`;

const ModalHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid ${p => p.theme.colors.gray300};
  display: flex;
  gap: 8px;
  padding: 16px 32px;
`;

const ModalHeaderIcon = styled(TeamAssistIcon)`
  color: ${p => p.theme.colors.purple400};
  flex-shrink: 0;
`;

const ModalTitle = styled.span`
  align-items: center;
  color: ${p => p.theme.colors.purple400};
  display: flex;
  font-size: 18px;
  font-weight: ${p => p.theme.fontWeight.medium};
  gap: 6px;
`;

const ModalSubtitle = styled.span`
  color: ${p => p.theme.colors.gray800};
  font-size: 12px;
  line-height: 1.2;
  margin-left: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ModalTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 360px;
  margin-right: 2px;
  min-width: 0;
`;

const ModalHeaderActions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray500};
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  &:hover {
    color: ${p => p.theme.colors.gray700};
  }
`;

const ModalMessagesArea = styled.div`
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior-y: none;
`;

const ModalMessagesInner = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 16px 32px;
`;

const ModalInputArea = styled.div`
  border-top: 1px solid ${p => p.theme.colors.gray300};
  padding: 16px 32px;
`;

const ModalInputWrapper = styled.div`
  position: relative;
`;

const ModalTextArea = styled(ExpandableTextArea)`
  background: ${p => p.theme.colors.gray100};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 24px;
  box-sizing: border-box;
  color: ${p => p.theme.colors.black};
  font-family: inherit;
  font-size: ${p => p.theme.fontSize.base};
  line-height: 20px;
  max-width: 100%;
  min-height: 48px;
  outline: none;
  padding: 13px 46px 13px 14px;
  width: 100%;
  &::placeholder {
    color: ${p => p.theme.colors.gray500};
  }
  &:focus {
    border-color: ${p => p.theme.colors.purple400};
  }
`;

const ModalSendButton = styled.button`
  align-items: center;
  background: ${p => (p.disabled ? p.theme.colors.gray300 : p.theme.colors.purple400)};
  border: none;
  border-radius: 50%;
  color: ${p => p.theme.colors.white};
  cursor: ${p => (p.disabled ? 'default' : 'pointer')};
  display: flex;
  height: 32px;
  justify-content: center;
  position: absolute;
  right: 8px;
  bottom: 14px;
  width: 32px;
  &:hover {
    background: ${p => (p.disabled ? p.theme.colors.gray300 : p.theme.colors.purple500)};
  }
`;

const EmptyState = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
  padding: 24px 16px;
  text-align: center;
`;

const EmptyStateIconWrapper = styled.div`
  color: ${p => p.theme.colors.purple400};
  margin-bottom: 16px;
  opacity: 0.85;
`;

const EmptyStateText = styled.p`
  color: ${p => p.theme.colors.gray800};
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.4;
  margin: 0;
  max-width: 260px;
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px 12px;
`;

const Dot = styled.div`
  animation: pulse 1s ease-in-out infinite;
  background: ${p => p.theme.colors.gray400};
  border-radius: 50%;
  height: 6px;
  width: 6px;

  @keyframes pulse {
    0%,
    80%,
    100% {
      opacity: 0.3;
    }
    40% {
      opacity: 1;
    }
  }
`;
